import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Plus, Trash2, Save, AlertCircle, User, 
  ChevronLeft, ChevronRight, Palmtree, X, Pencil
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Switch } from '../../../components/ui/switch';
import { companyApi } from '../../../lib/api';

const DAYS = [
  { value: 0, label: 'Lundi', short: 'Lun' },
  { value: 1, label: 'Mardi', short: 'Mar' },
  { value: 2, label: 'Mercredi', short: 'Mer' },
  { value: 3, label: 'Jeudi', short: 'Jeu' },
  { value: 4, label: 'Vendredi', short: 'Ven' },
  { value: 5, label: 'Samedi', short: 'Sam' },
  { value: 6, label: 'Dimanche', short: 'Dim' },
];

const HOURS = Array.from({ length: 12 }, (_, i) => {
  const hour = i + 8; // 8h to 19h
  return { value: `${hour.toString().padStart(2, '0')}:00`, label: `${hour}h00` };
});

export function CommercialAgendaPage() {
  const [slots, setSlots] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [commercial, setCommercial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddSlotOpen, setIsAddSlotOpen] = useState(false);
  const [isEditSlotOpen, setIsEditSlotOpen] = useState(false);
  const [isAddUnavailOpen, setIsAddUnavailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  
  // Form states
  const [newSlot, setNewSlot] = useState({ day_of_week: 0, start_time: '10:00', end_time: '12:00' });
  const [editSlots, setEditSlots] = useState([]); // Slots for the day being edited
  const [newUnavail, setNewUnavail] = useState({ 
    start_date: '', end_date: '', start_time: '', end_time: '', reason: '' 
  });

  const fetchData = async () => {
    try {
      const [slotsRes, unavailRes] = await Promise.all([
        companyApi.getCommercialSlots(),
        companyApi.getCommercialUnavailabilities()
      ]);
      setSlots(slotsRes.data.slots || []);
      setCommercial(slotsRes.data.commercial);
      setUnavailabilities(unavailRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAddSlot = async () => {
    setIsSubmitting(true);
    try {
      await companyApi.createCommercialSlot(newSlot);
      toast.success('Créneau ajouté');
      setIsAddSlotOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!confirm('Supprimer ce créneau ?')) return;
    try {
      await companyApi.deleteCommercialSlot(slotId);
      toast.success('Créneau supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Open edit modal for a specific day
  const openEditDay = (dayValue) => {
    const daySlots = slots.filter(s => s.day_of_week === dayValue);
    setEditingDay(dayValue);
    setEditSlots(daySlots.map(s => ({ ...s })));
    setIsEditSlotOpen(true);
  };

  // Add a new slot in edit mode
  const addEditSlot = () => {
    setEditSlots([...editSlots, { 
      id: `new-${Date.now()}`, 
      day_of_week: editingDay, 
      start_time: '10:00', 
      end_time: '12:00',
      isNew: true 
    }]);
  };

  // Remove a slot in edit mode
  const removeEditSlot = (slotId) => {
    setEditSlots(editSlots.filter(s => s.id !== slotId));
  };

  // Update a slot in edit mode
  const updateEditSlot = (slotId, field, value) => {
    setEditSlots(editSlots.map(s => 
      s.id === slotId ? { ...s, [field]: value } : s
    ));
  };

  // Save all edits for the day
  const handleSaveEditDay = async () => {
    setIsSubmitting(true);
    try {
      // Get original slots for this day
      const originalSlots = slots.filter(s => s.day_of_week === editingDay);
      
      // Delete removed slots
      for (const originalSlot of originalSlots) {
        if (!editSlots.find(s => s.id === originalSlot.id)) {
          await companyApi.deleteCommercialSlot(originalSlot.id);
        }
      }
      
      // Add new slots
      for (const editSlot of editSlots) {
        if (editSlot.isNew) {
          await companyApi.createCommercialSlot({
            day_of_week: editingDay,
            start_time: editSlot.start_time,
            end_time: editSlot.end_time,
            is_active: true
          });
        }
      }
      
      toast.success('Horaires mis à jour');
      setIsEditSlotOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUnavailability = async () => {
    if (!newUnavail.start_date || !newUnavail.end_date) {
      toast.error('Veuillez sélectionner les dates');
      return;
    }
    setIsSubmitting(true);
    try {
      await companyApi.createCommercialUnavailability(newUnavail);
      toast.success('Indisponibilité ajoutée');
      setIsAddUnavailOpen(false);
      setNewUnavail({ start_date: '', end_date: '', start_time: '', end_time: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUnavailability = async (unavailId) => {
    if (!confirm('Supprimer cette indisponibilité ?')) return;
    try {
      await companyApi.deleteCommercialUnavailability(unavailId);
      toast.success('Indisponibilité supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Group slots by day and expand to 1-hour slots
  const slotsByDay = DAYS.map(day => {
    const daySlots = slots.filter(s => s.day_of_week === day.value);
    
    // Expand each slot into 1-hour individual slots
    const expandedSlots = [];
    daySlots.forEach(slot => {
      const startHour = parseInt(slot.start_time.split(':')[0]);
      const endHour = parseInt(slot.end_time.split(':')[0]);
      
      for (let hour = startHour; hour < endHour; hour++) {
        expandedSlots.push({
          id: `${slot.id}-${hour}`,
          parentId: slot.id,
          start_time: `${hour.toString().padStart(2, '0')}:00`,
          end_time: `${(hour + 1).toString().padStart(2, '0')}:00`,
          display: `${hour}h - ${hour + 1}h`
        });
      }
    });
    
    return {
      ...day,
      slots: daySlots, // Original slots for deletion
      expandedSlots: expandedSlots // 1-hour slots for display
    };
  });

  // Sort unavailabilities by date
  const sortedUnavailabilities = [...unavailabilities].sort(
    (a, b) => new Date(a.start_date) - new Date(b.start_date)
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  if (!commercial && !isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Agenda Commercial</h1>
          <p className="text-muted-foreground mt-1">Gérez les créneaux de RDV du commercial</p>
        </div>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-orange-700 mb-2">Aucun commercial configuré</h3>
            <p className="text-orange-600 mb-4">
              Vous devez d'abord créer un collaborateur avec le rôle "Commercial" dans la section Collaborateurs.
            </p>
            <Button variant="outline" onClick={() => window.location.href = '/company?tab=collaborators'}>
              <User className="w-4 h-4 mr-2" />
              Aller aux Collaborateurs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Agenda Commercial</h1>
          <p className="text-muted-foreground mt-1">
            Gérez les créneaux de disponibilité {commercial && `de ${commercial.name}`}
          </p>
        </div>
        {commercial && (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl border border-blue-200">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-700">{commercial.name}</p>
              <p className="text-xs text-blue-500">Commercial</p>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="slots" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="slots" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Créneaux
          </TabsTrigger>
          <TabsTrigger value="unavailabilities" className="flex items-center gap-2">
            <Palmtree className="w-4 h-4" />
            Indisponibilités
          </TabsTrigger>
        </TabsList>

        {/* Créneaux Tab */}
        <TabsContent value="slots" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="font-heading">Créneaux de disponibilité</CardTitle>
                <CardDescription>Horaires de RDV disponibles pour les prospects</CardDescription>
              </div>
              <Button onClick={() => setIsAddSlotOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un créneau
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : (
                <div className="grid gap-4">
                  {slotsByDay.filter(day => day.value < 5 || day.expandedSlots.length > 0).map(day => (
                    <div key={day.value} className="flex items-center gap-4 p-4 border rounded-xl hover:bg-muted/30 transition-colors">
                      <div className="w-24 flex-shrink-0">
                        <p className="font-medium">{day.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {day.expandedSlots.length} RDV possible{day.expandedSlots.length > 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex-1 flex flex-wrap gap-2">
                        {day.expandedSlots.length === 0 ? (
                          <span className="text-sm text-muted-foreground italic">Aucun créneau configuré</span>
                        ) : (
                          day.expandedSlots.map(slot => (
                            <Badge key={slot.id} variant="secondary" className="gap-1 py-1.5 px-3 bg-green-50 text-green-700 border border-green-200">
                              <Clock className="w-3 h-3" />
                              {slot.display}
                            </Badge>
                          ))
                        )}
                      </div>
                      {/* Edit button for the day */}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDay(day.value)}
                        className="text-muted-foreground hover:text-primary"
                        title={`Modifier les horaires du ${day.label}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info box */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Chaque créneau = 1 RDV d'1 heure</p>
                <p className="text-blue-600">
                  Pour ajouter une plage horaire, cliquez sur "Ajouter un créneau". 
                  Une plage 10h-12h créera automatiquement 2 créneaux : 10h-11h et 11h-12h.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Indisponibilités Tab */}
        <TabsContent value="unavailabilities" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="font-heading">Périodes d'indisponibilité</CardTitle>
                <CardDescription>Congés, absences, formations...</CardDescription>
              </div>
              <Button onClick={() => setIsAddUnavailOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une période
              </Button>
            </CardHeader>
            <CardContent>
              {sortedUnavailabilities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Palmtree className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Aucune indisponibilité planifiée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedUnavailabilities.map(unavail => (
                    <div 
                      key={unavail.id} 
                      className="flex items-center justify-between p-4 border rounded-xl hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Palmtree className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {formatDate(unavail.start_date)}
                            {unavail.start_date !== unavail.end_date && (
                              <> → {formatDate(unavail.end_date)}</>
                            )}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {unavail.start_time && unavail.end_time ? (
                              <span>{unavail.start_time} - {unavail.end_time}</span>
                            ) : (
                              <span>Journée entière</span>
                            )}
                            {unavail.reason && (
                              <>
                                <span>•</span>
                                <span>{unavail.reason}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteUnavailability(unavail.id)}
                        className="hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Slot Dialog */}
      <Dialog open={isAddSlotOpen} onOpenChange={setIsAddSlotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter un créneau</DialogTitle>
            <DialogDescription>
              Définissez une plage horaire de disponibilité pour les RDV
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Jour de la semaine</Label>
              <Select 
                value={newSlot.day_of_week.toString()} 
                onValueChange={(v) => setNewSlot({...newSlot, day_of_week: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(day => (
                    <SelectItem key={day.value} value={day.value.toString()}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Heure de début</Label>
                <Select 
                  value={newSlot.start_time} 
                  onValueChange={(v) => setNewSlot({...newSlot, start_time: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(hour => (
                      <SelectItem key={hour.value} value={hour.value}>
                        {hour.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Heure de fin</Label>
                <Select 
                  value={newSlot.end_time} 
                  onValueChange={(v) => setNewSlot({...newSlot, end_time: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(hour => (
                      <SelectItem key={hour.value} value={hour.value}>
                        {hour.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddSlotOpen(false)}>Annuler</Button>
            <Button onClick={handleAddSlot} disabled={isSubmitting}>
              {isSubmitting ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Day Slots Dialog */}
      <Dialog open={isEditSlotOpen} onOpenChange={setIsEditSlotOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Modifier les horaires du {DAYS.find(d => d.value === editingDay)?.label}
            </DialogTitle>
            <DialogDescription>
              Configurez les plages horaires disponibles pour les RDV
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editSlots.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun créneau configuré pour ce jour
              </p>
            ) : (
              <div className="space-y-3">
                {editSlots.map((slot, index) => (
                  <div key={slot.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm font-medium w-16">Plage {index + 1}</span>
                    <Select 
                      value={slot.start_time} 
                      onValueChange={(v) => updateEditSlot(slot.id, 'start_time', v)}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map(hour => (
                          <SelectItem key={hour.value} value={hour.value}>
                            {hour.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground">→</span>
                    <Select 
                      value={slot.end_time} 
                      onValueChange={(v) => updateEditSlot(slot.id, 'end_time', v)}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map(hour => (
                          <SelectItem key={hour.value} value={hour.value}>
                            {hour.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeEditSlot(slot.id)}
                      className="text-muted-foreground hover:text-destructive ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" onClick={addEditSlot} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une plage horaire
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSlotOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveEditDay} disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Unavailability Dialog */}
      <Dialog open={isAddUnavailOpen} onOpenChange={setIsAddUnavailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter une indisponibilité</DialogTitle>
            <DialogDescription>
              Bloquez une période où le commercial n'est pas disponible
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input 
                  type="date" 
                  value={newUnavail.start_date}
                  onChange={(e) => setNewUnavail({...newUnavail, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input 
                  type="date" 
                  value={newUnavail.end_date}
                  onChange={(e) => setNewUnavail({...newUnavail, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Heure de début (optionnel)</Label>
                <Input 
                  type="time" 
                  value={newUnavail.start_time}
                  onChange={(e) => setNewUnavail({...newUnavail, start_time: e.target.value})}
                  placeholder="Vide = journée entière"
                />
              </div>
              <div className="space-y-2">
                <Label>Heure de fin (optionnel)</Label>
                <Input 
                  type="time" 
                  value={newUnavail.end_time}
                  onChange={(e) => setNewUnavail({...newUnavail, end_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Raison (optionnel)</Label>
              <Input 
                value={newUnavail.reason}
                onChange={(e) => setNewUnavail({...newUnavail, reason: e.target.value})}
                placeholder="Ex: Congés, Formation, RDV médical..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddUnavailOpen(false)}>Annuler</Button>
            <Button onClick={handleAddUnavailability} disabled={isSubmitting}>
              {isSubmitting ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CommercialAgendaPage;
